import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, ClipboardList, Printer } from "lucide-react";
import { ProjectBundleOverlay } from "@/components/ProjectReport";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { PageHeader, DataTable, StatusBadge, FormDialog, Field, Loading } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtTon } from "@/lib/utils";

const EMPTY = { kode: "", nama: "", tipe: "", targetTon: "", status: "Aktif", tanggalGambarTurun: "", targetPenyelesaian: "" };
const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const sisaHari = (d) => (d ? Math.ceil((new Date(d + "T00:00:00") - new Date()) / 86400000) : null);

export default function Projects() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => { api.get("/projects").then((r) => setRows(r.data)).catch((e) => toast.error(fmtErr(e))); }, []);
  useEffect(() => { load(); }, [load]);

  const startCreate = () => { setEdit(null); setForm(EMPTY); setOpen(true); };
  const [bundle, setBundle] = useState(null);
  const [bundleBusy, setBundleBusy] = useState(false);
  const printAll = async () => {
    setBundleBusy(true);
    try { const { data } = await api.get("/projects/report-bundle"); if (!data.count) toast.error("Tidak ada proyek aktif"); else setBundle(data); } catch (e) { toast.error(fmtErr(e)); }
    setBundleBusy(false);
  };
  const startEdit = (r) => { setEdit(r); setForm({ kode: r.kode, nama: r.nama, tipe: r.tipe || "", targetTon: r.targetTon, status: r.status, tanggalGambarTurun: r.tanggalGambarTurun || "", targetPenyelesaian: r.targetPenyelesaian || "" }); setOpen(true); };

  const submit = async () => {
    try {
      const payload = { ...form, targetTon: Number(form.targetTon) };
      if (edit) { await api.patch(`/projects/${edit.id}`, payload); toast.success("Proyek diperbarui / 项目已更新"); }
      else { await api.post("/projects", payload); toast.success("Proyek ditambahkan / 项目已创建"); }
      setOpen(false); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Hapus proyek ${r.nama}? SPK & marking terkait ikut terhapus.`)) return;
    try { await api.delete(`/projects/${r.id}`); toast.success("Proyek dihapus"); load(); } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-5">
      <PageHeader id="Master Projek" zh="项目主数据" desc="Data induk proyek: kode, nama, tonase kontrak, tanggal gambar turun & target penyelesaian (diisi admin)."
        actions={<>
          <Button onClick={printAll} disabled={bundleBusy} variant="outline" className="border-slate-700" data-testid="project-print-all-button"><Printer className="mr-1.5 h-4 w-4" />{bundleBusy ? "Menyiapkan…" : "Cetak Semua Proyek Aktif"}</Button>
          <Button onClick={startCreate} className="bg-sky-600 hover:bg-sky-500" data-testid="project-add-button"><Plus className="mr-1.5 h-4 w-4" />Tambah Proyek</Button>
        </>} />

      <div className="panel p-4" data-testid="projects-panel">
        {!rows ? <Loading /> : (
          <DataTable testid="projects-table" rows={rows} columns={[
            { key: "no", label: "No", render: (r, i) => <span className="font-mono text-slate-500">{i + 1}</span> },
            { key: "kode", label: "Kode Proyek", render: (r) => <span className="font-mono text-sky-400">{r.kode}</span> },
            { key: "nama", label: "Nama Proyek", render: (r) => <span>{r.nama} {r.tipe && <span className="cn text-[11px] text-slate-500">{r.tipe}</span>}</span> },
            { key: "targetTon", label: "Tonase Kontrak", render: (r) => <span className="font-mono">{fmtTon(r.targetTon)} <span className="text-[10px] text-slate-500">Ton</span></span> },
            { key: "progres", label: "Progres vs Kontrak", render: (r) => (
              <div className="w-40 space-y-1" data-testid={`project-progress-${r.kode}`}>
                {[["DL", r.dlPct, r.dlTon, "bg-sky-500", "text-sky-300"], ["STB", r.stbPct, r.stbTon, "bg-indigo-500", "text-indigo-300"]].map(([t, pct, ton, bar, txt]) => (
                  <div key={t} className="flex items-center gap-1.5 text-[10px]">
                    <span className="w-7 font-mono text-slate-500">{t}</span>
                    <div className="h-1.5 flex-1 overflow-hidden rounded bg-slate-800"><div className={`h-full ${bar}`} style={{ width: `${Math.min(100, pct || 0)}%` }} /></div>
                    <span className={`w-20 text-right font-mono ${txt}`}>{fmtTon(ton || 0)} T · {pct || 0}%</span>
                  </div>
                ))}
              </div>
            )},
            { key: "tanggalGambarTurun", label: "Tgl Gambar Turun", render: (r) => <span className="font-mono text-xs text-slate-300" data-testid={`project-gambar-${r.kode}`}>{fmtDate(r.tanggalGambarTurun)}</span> },
            { key: "targetPenyelesaian", label: "Target Penyelesaian", render: (r) => {
              const sisa = sisaHari(r.targetPenyelesaian);
              return (
                <span className="inline-flex flex-col" data-testid={`project-target-${r.kode}`}>
                  <span className="font-mono text-xs text-slate-300">{fmtDate(r.targetPenyelesaian)}</span>
                  {sisa != null && r.status !== "Selesai" && <span className={`text-[10px] ${sisa < 0 ? "text-rose-400" : sisa <= 30 ? "text-amber-400" : "text-slate-500"}`}>{sisa < 0 ? `terlambat ${-sisa} hari` : `sisa ${sisa} hari`}</span>}
                </span>
              );
            }},
            { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
            { key: "aksi", label: "Aksi", render: (r) => (
              <span className="flex gap-1.5">
                <Link to={`/delivery-list-proyek?project=${r.id}`} title="Delivery List proyek" className="inline-flex h-8 w-8 items-center justify-center rounded hover:bg-slate-800" data-testid={`project-dl-${r.kode}`}><ClipboardList className="h-3.5 w-3.5 text-sky-400" /></Link>
                <Button size="sm" variant="ghost" onClick={() => startEdit(r)} data-testid={`project-edit-${r.kode}`}><Pencil className="h-3.5 w-3.5 text-slate-400" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(r)} data-testid={`project-delete-${r.kode}`}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>
              </span>
            )},
          ]} />
        )}
      </div>

      <FormDialog open={open} onOpenChange={setOpen} testid="project-dialog"
        title={edit ? `Edit Proyek · ${edit.kode}` : "Tambah Proyek / 新建项目"} onSubmit={submit} submitLabel={edit ? "Simpan" : "Tambah"}>
        <Field label="Kode Proyek"><Input required value={form.kode} onChange={(e) => setForm({ ...form, kode: e.target.value })} placeholder="PJ-2026-013" data-testid="project-kode-input" /></Field>
        <Field label="Nama Proyek"><Input required value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="GUDANG KARGO" data-testid="project-nama-input" /></Field>
        <Field label="Tipe / 型别"><Input value={form.tipe} onChange={(e) => setForm({ ...form, tipe: e.target.value })} placeholder="WAREHOUSE / 300型窗分" data-testid="project-tipe-input" /></Field>
        <Field label="Tonase Kontrak (Ton)"><Input required type="number" min="1" step="0.1" value={form.targetTon} onChange={(e) => setForm({ ...form, targetTon: e.target.value })} placeholder="250" data-testid="project-target-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tanggal Gambar Turun / 图纸下发日期"><Input type="date" value={form.tanggalGambarTurun} onChange={(e) => setForm({ ...form, tanggalGambarTurun: e.target.value })} data-testid="project-gambar-input" /></Field>
          <Field label="Target Penyelesaian / 目标完成日期"><Input type="date" value={form.targetPenyelesaian} onChange={(e) => setForm({ ...form, targetPenyelesaian: e.target.value })} data-testid="project-selesai-input" /></Field>
        </div>
        <Field label="Status">
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })} data-testid="project-status-select">
            <SelectTrigger className="border-slate-800 bg-slate-900/70"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0D1524] border-slate-800">
              <SelectItem value="Aktif">Aktif</SelectItem>
              <SelectItem value="Selesai">Selesai</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </FormDialog>
      {bundle && <ProjectBundleOverlay bundle={bundle} onClose={() => setBundle(null)} />}
    </div>
  );
}